import { PLAN_ANNUAL, PLAN_MONTHLY } from '../../Constants/plans'
import { TIER_ESSENTIALS, TIER_PREMIUM, UPGRADE_PLANS } from '../Constants/upgradePlans'

const {
  ESSENTIALS_MONTHLY, ESSENTIALS_ANNUAL, PREMIUM_MONTHLY, PREMIUM_ANNUAL,
} = UPGRADE_PLANS

const scenarioKey = (current, target) => `${current}->${target}`

const PLAN_OFFERS = {
  [ESSENTIALS_MONTHLY]: { price: 9.99, perModelPrice: 0.4, freeMonths: null },
  [ESSENTIALS_ANNUAL]: { price: 99.99, perModelPrice: 0.33, freeMonths: 2 },
  [PREMIUM_MONTHLY]: { price: 34.99, perModelPrice: 1.4, freeMonths: null },
  [PREMIUM_ANNUAL]: { price: 349.99, perModelPrice: 1.17, freeMonths: 2 },
}

const TIER_ALLOWANCE = {
  [TIER_ESSENTIALS]: { downloadsPerMonth: 25, generationsPerMonth: 25 },
  [TIER_PREMIUM]: { downloadsPerMonth: 25, generationsPerMonth: 100 },
}

const CATALOG_VALUE = { [TIER_ESSENTIALS]: '840K', [TIER_PREMIUM]: '1.7M' }

// Allowed upgrade targets per current plan, in the contract's order (124g39pcvuz, "Targets").
const TARGETS_BY_CURRENT = {
  [ESSENTIALS_MONTHLY]: [PREMIUM_MONTHLY, PREMIUM_ANNUAL, ESSENTIALS_ANNUAL],
  [ESSENTIALS_ANNUAL]: [PREMIUM_MONTHLY, PREMIUM_ANNUAL],
  [PREMIUM_MONTHLY]: [PREMIUM_ANNUAL],
}

// Amounts from the design ("Elements: Order Summary"); scenarios without a design use the same formula.
const SCENARIOS = {
  [scenarioKey(ESSENTIALS_MONTHLY, PREMIUM_MONTHLY)]: {
    units: { used: 5, total: 25 }, credit: { amount: 7.99, planType: PLAN_MONTHLY }, vatAmount: 5.4, amountDue: 32.4,
  },
  [scenarioKey(ESSENTIALS_MONTHLY, PREMIUM_ANNUAL)]: {
    units: { used: 5, total: 25 }, credit: { amount: 7.99, planType: PLAN_MONTHLY }, vatAmount: 68.4, amountDue: 410.4,
  },
  [scenarioKey(ESSENTIALS_MONTHLY, ESSENTIALS_ANNUAL)]: {
    units: { used: 5, total: 25 }, credit: { amount: 7.99, planType: PLAN_MONTHLY }, vatAmount: 18.4, amountDue: 110.4,
  },
  [scenarioKey(ESSENTIALS_ANNUAL, PREMIUM_ANNUAL)]: {
    units: { used: 160, total: 300 },
    credit: { amount: 46.66, planType: PLAN_ANNUAL },
    vatAmount: 60.67,
    amountDue: 364,
  },
  [scenarioKey(PREMIUM_MONTHLY, PREMIUM_ANNUAL)]: {
    units: { used: 5, total: 25 }, credit: { amount: 27.99, planType: PLAN_MONTHLY }, vatAmount: 64.4, amountDue: 386.4,
  },
}

const DEFAULT_CURRENT = ESSENTIALS_MONTHLY

function parsePlan(plan) {
  const [tier, planType] = plan.split('_')
  return { tier, planType }
}

function toAllowedTarget(current, target) {
  const { tier, planType } = parsePlan(target)
  const isBlocked = parsePlan(current).planType === PLAN_ANNUAL && planType === PLAN_MONTHLY

  return {
    target,
    tier,
    planType,
    ...PLAN_OFFERS[target],
    ...TIER_ALLOWANCE[tier],
    available: !isBlocked,
    blockedReason: isBlocked ? 'annual_to_monthly' : null,
  }
}

function addMonths(date, months) {
  const next = new Date(date)
  next.setUTCMonth(next.getUTCMonth() + months)
  return next
}

// Missing or blocked targets resolve to the first available one, as the BE does.
function resolveTarget(requested, allowedTargets) {
  const available = allowedTargets.filter((target) => target.available)
  return (available.find((target) => target.target === requested) || available[0]).target
}

// Test fixture: an upgrade quote in the shape of contract 124g39pcvuz (worked examples of the contract).
export default function buildUpgradeQuoteFixture({
  current = DEFAULT_CURRENT, target = null, now = new Date(),
} = {}) {
  const allowedTargets = TARGETS_BY_CURRENT[current].map((plan) => toAllowedTarget(current, plan))
  const resolvedTarget = resolveTarget(target, allowedTargets)
  const targetPlan = parsePlan(resolvedTarget)

  return {
    target: resolvedTarget,
    currency: 'USD',
    current: { ...parsePlan(current), price: PLAN_OFFERS[current].price },
    targetPlan: { ...targetPlan, price: PLAN_OFFERS[resolvedTarget].price },
    ...SCENARIOS[scenarioKey(current, resolvedTarget)],
    newTermStartsAt: now.toISOString(),
    nextRenewalAt: addMonths(now, targetPlan.planType === PLAN_ANNUAL ? 12 : 1).toISOString(),
    catalogValue: CATALOG_VALUE[targetPlan.tier],
    allowedTargets,
  }
}
